/**
 * SPEC-UnifiedQADetail — view-mode primitive.
 *
 * `viewMode` is the layout primitive. It is a `'agent' | 'senior'`
 * preference stored in `localStorage` under `qaViewMode`. It does NOT
 * convey permission — permission gates always run against the real user's
 * `role` / `isAdmin` from `useRole()`.
 *
 * These helpers are pure so they can be unit-tested without jsdom (the
 * repo's existing test convention — see `vitest.server.config.ts` header).
 * The React wrapper in `client/src/hooks/use-view-mode.ts` layers
 * localStorage + storage-event sync on top.
 *
 * Locked rules:
 *   - Default for `role === 'agent'` is `'agent'`. Agents cannot toggle.
 *   - Default for `role === 'senior' | 'admin' | 'bot' | null` is `'senior'`.
 *   - Stored value MUST be one of `'agent' | 'senior'`. Anything else falls
 *     back to the role-derived default.
 *   - Agent role + stored `'senior'` still resolves to `'agent'` (agent pin
 *     overrides storage).
 *   - `null` role (pre-resolution) ignores stored values to avoid flashing
 *     the agent surface before auth lands.
 */

import type { Role } from "./role-helpers";

export type ViewMode = "agent" | "senior";

export const VIEW_MODE_STORAGE_KEY = "qaViewMode";

/**
 * The role-derived default when localStorage is empty / unset. Used by
 * `coerceStoredViewMode` and surfaced separately for callers that want the
 * "what would the default be" answer without rationalizing a stored value.
 */
export function defaultViewMode(role: Role | null): ViewMode {
  return role === "agent" ? "agent" : "senior";
}

/**
 * True only for `'senior'` and `'admin'` — they're the roles the toggle
 * surfaces in the UI for. Agents are pinned; bots/null have no toggle.
 */
export function canToggleViewMode(role: Role | null): boolean {
  return role === "senior" || role === "admin";
}

/**
 * Coerce a raw value from localStorage (or any external source) into a valid
 * `ViewMode`, applying the agent pin and the null-role guard.
 *
 *   - Agent role: result is ALWAYS `'agent'` (stored values are ignored).
 *   - Toggle-capable role + valid stored value: returns the stored value.
 *   - Toggle-capable role + invalid stored value: returns role-derived default.
 *   - null/bot role: returns role-derived default (stored values ignored).
 */
export function coerceStoredViewMode(
  stored: string | null | undefined,
  role: Role | null,
): ViewMode {
  if (role === "agent") return "agent";
  if (!canToggleViewMode(role)) return defaultViewMode(role);
  if (stored === "agent" || stored === "senior") return stored;
  return defaultViewMode(role);
}
